import { isInIframe } from '@telecom/shared/utils/in-iframe';
import { sendRouterChangeMessage } from '@/utils/send-parent-message';
import type { ComposerTranslation } from 'vue-i18n';

/**
 * 卡片点击参数
 */
export interface CardClickParams {
    /**
     * 功能ID
     */
    functionId: string;

    /**
     * Agent ID（Agent 类型卡片使用）
     */
    agentId?: string;

    /**
     * Team ID（Agent Team 类型卡片使用）
     */
    teamId?: string;
}

/**
 * Playbook 处理器接口
 * 定义卡片的样式（外观）和行为（交互）
 */
export interface PlaybookHandler {
    /**
     * 获取卡片名称
     * @param t - i18n 翻译函数
     */
    getName(t: ComposerTranslation): string;

    /**
     * 获取卡片描述
     * @param t - i18n 翻译函数
     */
    getDescription(t: ComposerTranslation): string;

    /**
     * 获取卡片图标
     * @returns SVG sprite ID，如果返回 undefined 则不显示图标
     */
    getIcon?(): string | undefined;

    /**
     * 处理卡片点击事件
     * @param params - 卡片点击参数，包含 functionId、agentId、teamId 等信息
     * @returns Promise，如果需要异步处理（如弹窗确认），返回 Promise
     */
    handleClick(params: CardClickParams): void | Promise<void>;
}

/**
 * 基础 Playbook 处理器
 * 提供默认实现
 */
export abstract class BasePlaybookHandler implements PlaybookHandler {
    constructor(
        protected readonly i18nKey: string,
        protected readonly icon?: string,
    ) {}

    getName(t: ComposerTranslation): string {
        return t(`playbook.${this.i18nKey}.title`);
    }

    getDescription(t: ComposerTranslation): string {
        return t(`playbook.${this.i18nKey}.desc`);
    }

    getIcon(): string | undefined {
        return this.icon;
    }

    abstract handleClick(params: CardClickParams): void | Promise<void>;
}

/**
 * 简单路由跳转的 Playbook 处理器
 * 用于大多数标准卡片
 */
export class SimplePlaybookHandler extends BasePlaybookHandler {
    constructor(
        i18nKey: string,
        icon: string,
        private readonly pathFactory: () => string,
    ) {
        super(i18nKey, icon);
    }

    handleClick(_params: CardClickParams): void {
        const path = this.pathFactory();
        if (!path) {
            return;
        }

        if (isInIframe()) {
            // 在 iframe 中，通过 postMessage 通知父窗口跳转
            sendRouterChangeMessage({ path });
        } else {
            // 不在 iframe 中，暂不支持
            console.warn('暂不支持在非 iframe 环境中跳转:', path);
        }
    }
}

/**
 * 自定义处理器的 Playbook
 * 用于需要特殊交互的卡片（如弹窗确认）
 */
export class CustomPlaybookHandler extends BasePlaybookHandler {
    constructor(
        i18nKey: string,
        private readonly customHandler: (params: CardClickParams) => void | Promise<void>,
        icon?: string,
    ) {
        super(i18nKey, icon);
    }

    handleClick(params: CardClickParams): void | Promise<void> {
        return this.customHandler(params);
    }
}
